import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Chip, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import React, { FC, MouseEvent } from 'react';
import strings from '../../../../common/Translation/Translate';

/** 0–84% red, 85–89% amber, 90–100% green */
export const getVenueConfigHealthProgressColor = (
  percent: number,
): 'error' | 'warning' | 'success' => {
  const rounded = Math.round(percent);
  if (rounded < 85) return 'error';
  if (rounded < 90) return 'warning';
  return 'success';
};

export type VenueConfigurationHealthItem = {
  label: string;
  complete: boolean;
};

const buildHealthItems = (
  configurationHealth: VenueConfigurationHealthItem[],
  missingFields: string[],
): VenueConfigurationHealthItem[] =>
  configurationHealth.length > 0
    ? configurationHealth
    : missingFields.map(label => ({ label, complete: false }));

type VenueConfigurationHealthTooltipContentProps = {
  value: number;
  configurationHealth?: VenueConfigurationHealthItem[];
  missingFields?: string[];
};

export const VenueConfigurationHealthTooltipContent: FC<VenueConfigurationHealthTooltipContentProps> = ({
  value,
  configurationHealth = [],
  missingFields = [],
}) => {
  const rounded = Math.round(value);
  const healthItems = buildHealthItems(configurationHealth, missingFields);

  return (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {strings.venueConfig_health_title.replace('{percent}', String(rounded))}
      </Typography>
      {healthItems.map(item => (
        <Box
          key={item.label}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
          {item.complete ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
          ) : (
            <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          )}
          <Typography variant="body2">{item.label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const healthTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      boxShadow: 3,
      border: 1,
      borderColor: 'divider',
      maxWidth: 300,
      p: 1.5,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
      '&::before': {
        border: 1,
        borderColor: 'divider',
      },
    },
  },
};

type VenueConfigurationHealthPanelProps = {
  value: number;
  configurationHealth?: VenueConfigurationHealthItem[];
  missingFields?: string[];
};

export const VenueConfigurationHealthPanel: FC<VenueConfigurationHealthPanelProps> = ({
  value,
  configurationHealth = [],
  missingFields = [],
}) => {
  const rounded = Math.round(value);
  const progressColor = getVenueConfigHealthProgressColor(rounded);
  const healthItems = buildHealthItems(configurationHealth, missingFields);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        mb: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        bgcolor: 'background.paper',
      }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0, mt: 0.25 }}>
        <CircularProgress
          variant="determinate"
          value={rounded}
          size={52}
          thickness={4}
          color={progressColor}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
            {rounded}%
          </Typography>
        </Box>
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {strings.venueConfig_health_title.replace('{percent}', String(rounded))}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {healthItems.map(item => (
            <Chip
              key={item.label}
              size="small"
              variant="outlined"
              icon={
                item.complete ? (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                ) : (
                  <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                )
              }
              label={item.label}
              sx={{
                fontWeight: 500,
                bgcolor: 'background.paper',
                borderColor: 'divider',
                color: 'text.primary',
                '& .MuiChip-icon': {
                  color: item.complete ? 'success.main' : 'warning.main',
                  opacity: 1,
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

type VenueConfigurationHealthCellProps = {
  value: number;
  configurationHealth?: VenueConfigurationHealthItem[];
  missingFields?: string[];
};

export const VenueConfigurationHealthCell: FC<VenueConfigurationHealthCellProps> = ({
  value,
  configurationHealth = [],
  missingFields = [],
}) => {
  const rounded = Math.round(value);
  const progressColor = getVenueConfigHealthProgressColor(rounded);

  const stopRowNavigation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Tooltip
      arrow
      placement="left"
      slotProps={healthTooltipSlotProps}
      title={
        <VenueConfigurationHealthTooltipContent
          value={value}
          configurationHealth={configurationHealth}
          missingFields={missingFields}
        />
      }>
      <Box
        component="span"
        onClick={stopRowNavigation}
        sx={{ position: 'relative', display: 'inline-flex', cursor: 'default' }}>
        <CircularProgress
          variant="determinate"
          value={rounded}
          size={36}
          thickness={4}
          color={progressColor}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
            {rounded}%
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};
